import React, { useEffect, useState, useMemo } from 'react';
import { courseService } from '../../services/course.service';
import { UWSubject, UWCourse } from '../../types';
import { SearchIcon, CloseIcon, BookIcon, ChevronDownIcon } from '../UI/Icons';

const COMPONENT_MAP: Record<string, string> = {
  LEC: 'Lecture',
  LAB: 'Lab',
  TUT: 'Tutorial',
  PRJ: 'Project',
  SEM: 'Seminar',
  STU: 'Studio',
};

const CAREER_MAP: Record<string, string> = {
  UG: 'Undergraduate',
  GRD: 'Graduate',
};

const CourseDetailModal: React.FC<{ course: UWCourse; onClose: () => void }> = ({
  course,
  onClose,
}) => (
  <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="fixed inset-0 bg-canvas-900/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl rounded-3xl bg-white p-7 shadow-shell sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="chip bg-primary-50 text-primary-700">
              {course.subjectCode} {course.catalogNumber}
            </span>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-canvas-900">
              {course.title}
            </h2>
            <p className="mt-1 text-sm text-canvas-500">{course.termName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-canvas-500 transition-colors hover:bg-canvas-100 hover:text-canvas-900"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {course.associatedAcademicCareer && (
            <span className="chip bg-canvas-100 text-canvas-700">
              {CAREER_MAP[course.associatedAcademicCareer] ?? course.associatedAcademicCareer}
            </span>
          )}
          {course.courseComponentCode && (
            <span className="chip bg-canvas-100 text-canvas-700">
              {COMPONENT_MAP[course.courseComponentCode] ?? course.courseComponentCode}
            </span>
          )}
          {course.gradingBasis && (
            <span className="chip bg-canvas-100 text-canvas-700">
              {course.gradingBasis === 'NUM' ? 'Numeric grade' : course.gradingBasis}
            </span>
          )}
        </div>

        {course.description && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-canvas-500">
              Description
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-canvas-700">{course.description}</p>
          </div>
        )}

        {course.requirementsDescription && (
          <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-800">
              Prerequisites &amp; requirements
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-amber-800">
              {course.requirementsDescription}
            </p>
          </div>
        )}

        {(course.enrollConsentCode !== 'N' || course.dropConsentCode !== 'N') && (
          <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {course.enrollConsentCode !== 'N' && <p>Enroll: {course.enrollConsentDescription}</p>}
            {course.dropConsentCode !== 'N' && <p>Drop: {course.dropConsentDescription}</p>}
          </div>
        )}
      </div>
    </div>
  </div>
);

const CourseCard: React.FC<{ course: UWCourse; onClick: () => void }> = ({ course, onClick }) => (
  <button
    onClick={onClick}
    className="card group p-5 text-left transition-all hover:-translate-y-1 hover:border-primary-200 hover:shadow-card-hover"
  >
    <div className="flex items-start justify-between gap-3">
      <span className="chip bg-primary-50 text-primary-700">
        {course.subjectCode} {course.catalogNumber}
      </span>
      <span className="shrink-0 text-xs text-canvas-500">
        {COMPONENT_MAP[course.courseComponentCode] ?? course.courseComponentCode}
      </span>
    </div>

    <h3 className="mt-3 font-display text-base font-semibold leading-snug text-canvas-900">
      {course.title}
    </h3>

    {course.description && (
      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-canvas-600">
        {course.description}
      </p>
    )}

    {course.requirementsDescription && (
      <p className="mt-3 line-clamp-1 text-xs text-amber-700">
        {course.requirementsDescription}
      </p>
    )}
  </button>
);

const CourseList: React.FC = () => {
  const [subjects, setSubjects] = useState<UWSubject[]>([]);
  const [courses, setCourses] = useState<UWCourse[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('CS');
  const [search, setSearch] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<UWCourse | null>(null);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    courseService
      .getSubjects()
      .then(setSubjects)
      .catch(() => setError('Failed to load subjects'))
      .finally(() => setLoadingSubjects(false));
  }, []);

  useEffect(() => {
    if (!selectedSubject) return;
    setLoadingCourses(true);
    setError(null);
    setCourses([]);
    courseService
      .getUWCourses(selectedSubject)
      .then(setCourses)
      .catch(() => setError(`Failed to load courses for ${selectedSubject}`))
      .finally(() => setLoadingCourses(false));
  }, [selectedSubject]);

  const filteredCourses = useMemo(() => {
    if (!search.trim()) return courses;
    const q = search.toLowerCase();
    return courses.filter(
      (c) =>
        c.catalogNumber.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
    );
  }, [courses, search]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900 sm:text-3xl">
            Course Catalog
          </h1>
          <p className="mt-1.5 text-sm text-canvas-600">
            Live course data from the University of Waterloo
            {courses.length > 0 && ` · ${filteredCourses.length} of ${courses.length} in ${selectedSubject}`}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative sm:w-64">
          {loadingSubjects ? (
            <div className="h-[42px] animate-pulse rounded-xl bg-canvas-200" />
          ) : (
            <>
              <select
                value={selectedSubject}
                onChange={(e) => {
                  setSelectedSubject(e.target.value);
                  setSearch('');
                }}
                className="input-field appearance-none pr-10"
              >
                {subjects.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.description}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                width={16}
                height={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-canvas-500"
              />
            </>
          )}
        </div>

        <div className="relative flex-1">
          <SearchIcon
            width={17}
            height={17}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-canvas-500"
          />
          <input
            type="text"
            placeholder="Search by number, title, or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-11"
          />
        </div>
      </div>

      {/* Body */}
      <div className="mt-6">
        {error ? (
          <div className="card px-6 py-16 text-center">
            <p className="text-sm text-rose-600">{error}</p>
          </div>
        ) : loadingCourses ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl bg-canvas-200/70" />
            ))}
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="card flex flex-col items-center px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-canvas-100 text-canvas-500">
              <BookIcon width={26} height={26} />
            </span>
            <p className="mt-4 font-medium text-canvas-900">No courses found</p>
            <p className="mt-1 text-sm text-canvas-600">
              {search
                ? `Nothing matches “${search}” in ${selectedSubject}`
                : `No ${selectedSubject} courses are offered this term`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredCourses.map((course) => (
              <CourseCard
                key={`${course.courseId}-${course.courseOfferNumber}`}
                course={course}
                onClick={() => setSelectedCourse(course)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedCourse && (
        <CourseDetailModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />
      )}
    </div>
  );
};

export default CourseList;
